import { Context, type Effect } from 'effect';

import type { Page, PageRequest } from '@vs-shop/domain/pagination';

import type { ProductSummary } from '../productSchema';
import type { CategoryListingError } from './errors';
import type { TaxonNode } from './taxonNodeSchema';

/**
 * What a category listing reads: one taxon, page by page.
 *
 * `taxonCode` is the taxon's own code, not a context id, an IRI or a filter
 * expression. A backend that needs one of those builds it in its adapter; the
 * syntax a backend filters with never leaves it.
 *
 * Narrowing by facet, price bound or order is not here. A filtered or ordered
 * read goes through `ProductListing`. A request that can only ever page a taxon
 * is a request every tenant can honour, which is the property this type exists
 * to keep.
 */
export type TaxonPageRequest = PageRequest & {
  readonly taxonCode: string;
};

/**
 * One category: the taxon itself, and the products filed under it.
 *
 * Two operations rather than one, and that split is load-bearing. A category
 * page has to know a taxon's shape before it knows whether its listing matters:
 * a taxon with children is shown as those children, and a taxon without them as
 * its products. Folding that decision into a single `category(code)` operation
 * would put a display rule inside the port, where two tenants would then have to
 * agree on it. Keeping the two apart lets the loader read both at once and
 * decide afterwards, which is where the decision belongs.
 *
 * `products` answers a {@link Page}, so the loader reads the count the grid
 * pages through rather than the taxon's descendant rollup. Those are different
 * numbers.
 */
export type CategoryListingService = {
  readonly taxon: (code: string) => Effect.Effect<TaxonNode, CategoryListingError>;
  readonly products: (
    request: TaxonPageRequest
  ) => Effect.Effect<Page<ProductSummary>, CategoryListingError>;
};

export class CategoryListing extends Context.Service<CategoryListing, CategoryListingService>()(
  '@engine/Capability/CategoryListing'
) {}
